/**
 * src/composables/useUtils.js
 * * Este composable centraliza funciones de utilidad global, como formato de moneda y fechas.
 */
// Nota: No necesitamos 'ref' ni 'reactive' si solo exportamos funciones puras.

/**
 * Formatea un valor numérico como cadena de moneda, usando un símbolo configurable.
 * @param {number} value - El valor a formatear.
 * @param {string} [currencyCode='USD'] - Código ISO de la moneda (usado para Intl, aunque el símbolo es custom).
 * @param {string} [currencySymbol='$'] - Símbolo de moneda a prependizar.
 * @returns {string} El valor formateado con el símbolo.
 */
export function formatCurrency(value, currencyCode = 'USD', currencySymbol = '$') {
    if (value === null || isNaN(value)) {
        value = 0;
    }

    if (!currencyCode){
        currencyCode='UDS'
    }
    //console.log(currencyCode); // Comentamos el console.log para mantener limpio el entorno de producción

    // Usamos Intl.NumberFormat para el formato de miles/decimales
    const formattedValue = new Intl.NumberFormat('en-US', {
        // La configuración 'en-US' generalmente usa coma para miles y punto para decimales,
        // lo cual es común en la mayoría de los sistemas POS.
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
    }).format(value);

    return `${currencySymbol} ${formattedValue}`;
}

/**
 * Formatea una cadena de fecha a un formato local legible (dd/mm/yyyy hh:mm).
 * @param {string} dateString - La cadena de fecha (ej: ISO string).
 * @returns {string} La fecha y hora formateadas.
 */
export function formatDate(dateString) {
    if (!dateString) {
        return 'Fecha no disponible';
    }

    // Dividimos el string para tratar los componentes por separado y evitar el desfase UTC
    // Esto funciona para formatos YYYY-MM-DD o DD/MM/YYYY
    const parts = dateString.split(/[-/ :]/);
    const year = parts[0].length === 4 ? parts[0] : parts[2];
    const month = parts[0].length === 4 ? parts[1] : parts[1];
    const day = parts[0].length === 4 ? parts[2] : parts[0];

    // Creamos la fecha usando el constructor de componentes locales (año, mes - 1, día)
    const date = new Date(year, month - 1, day);

    // Si el string original incluía hora, se la asignamos manualmente
    if (parts.length >= 5) {
        date.setHours(parts[3], parts[4]);
    }

    if (isNaN(date)) {
        return 'Fecha inválida';
    }

    // Retornamos el formato local
    return date.toLocaleString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        // Solo incluimos la hora si el string original la tenía
        ...(parts.length >= 5 && { hour: '2-digit', minute: '2-digit' })
    });
}

export function currentLocalDate(date = new Date()){
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Mesi (0-11) + 1
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
}

/**
 * Carga una imagen de una URL y la convierte a una cadena Base64.
 * Esto es necesario para añadir imágenes externas a jsPDF.
 * * @param {string} url - La URL de la imagen a cargar (ej: logoUrl).
 * @param {string} [mimeType='image/png'] - El tipo MIME de la imagen (ej: 'image/jpeg', 'image/png').
 * @returns {Promise<string|null>} La cadena Base64 o null si falla.
 */
export const getImageBase64 = (url, mimeType = 'image/png') => {
    if (!url) return Promise.resolve(null);

    return new Promise((resolve) => {
        const img = new Image();

        // Permite cargar imágenes de dominios diferentes (CORS)
        img.crossOrigin = 'Anonymous';

        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);

                // Devuelve la Data URL (Base64) con el tipo MIME especificado
                resolve(canvas.toDataURL(mimeType));
            } catch (error) {
                console.error("Error processing image canvas:", error);
                resolve(null);
            }
        };

        img.onerror = () => {
            console.warn(`Failed to load image from URL: ${url}`);
            resolve(null); // Resuelve a null si la carga falla
        };

        img.src = url;
    });
};
export default {
    install(app) {
        // Exponerlas en las propiedades globales
        app.config.globalProperties.$formatCurrency = formatCurrency;
        app.config.globalProperties.$formatDate = formatDate;
        app.config.globalProperties.$getImageBase64 = getImageBase64;
        app.config.globalProperties.$currentLocalDate = currentLocalDate;
    }
};